import { PageContext, PageType, FormInfo, CTAInfo } from '@/types/pageContext';

class ContextExtractionService {
  private cache = new Map<string, { context: PageContext; timestamp: number }>();
  private readonly cacheTimeout = 5 * 60 * 1000; // 5 minutes

  extractPageContext(): PageContext {
    const url = window.location.pathname;
    const cacheKey = `${url}-${this.getContentHash()}`;
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.context;
    }

    const context: PageContext = {
      url,
      title: this.extractTitle(),
      content: this.extractMainContent(),
      pageType: this.determinePageType(url),
      features: this.extractFeatures(),
      forms: this.extractForms(),
      ctas: this.extractCTAs(),
      extractedAt: new Date(),
      hash: cacheKey
    };

    // Cache the result
    this.cache.set(cacheKey, { context, timestamp: Date.now() });
    
    // Clean old cache entries
    this.cleanCache();

    return context;
  }

  private extractTitle(): string {
    const h1 = document.querySelector('h1');
    if (h1) return h1.textContent?.trim() || '';
    
    const title = document.title;
    if (title && title !== 'Justiçai') return title;
    
    return 'Página Atual';
  }

  private extractMainContent(): string {
    // Remove script, style, and navigation elements
    const excludeSelectors = 'script, style, nav, .sidebar, .navbar, .footer, .cookie-banner';
    const main = document.querySelector('main') || document.body;
    
    // Clone to avoid modifying original DOM
    const clone = main.cloneNode(true) as Element;
    
    // Remove excluded elements
    clone.querySelectorAll(excludeSelectors).forEach(el => el.remove());
    
    // Get text content and clean it
    let content = clone.textContent || '';
    
    // Clean up whitespace and limit length
    content = content
      .replace(/\s+/g, ' ')
      .replace(/\n+/g, ' ')
      .trim()
      .substring(0, 2000); // Limit to 2000 chars for API efficiency
    
    return content;
  }

  private determinePageType(url: string): PageType {
    if (url.includes('/painel') || url === '/') return 'dashboard';
    if (url.includes('/chat')) return 'chat';
    if (url.includes('/documentos')) return 'documents';
    if (url.includes('/calculos')) return 'calculators';
    if (url.includes('/configuracoes')) return 'settings';
    if (url.includes('/planos')) return 'plans';
    if (url.includes('/legal')) return 'legal';
    if (url.includes('/notificacoes')) return 'notifications';
    if (url.includes('/tutoriais')) return 'tutorials';
    if (url.includes('/admin')) return 'admin';
    return 'unknown';
  }

  private extractFeatures(): string[] {
    const features: string[] = [];
    
    // Look for common UI elements that indicate features
    if (document.querySelector('[type="file"]')) features.push('Upload de Arquivos');
    if (document.querySelector('button[type="submit"]')) features.push('Formulários');
    if (document.querySelector('.editor, [contenteditable]')) features.push('Editor de Texto');
    if (document.querySelector('input[type="date"]')) features.push('Seleção de Data');
    if (document.querySelector('select')) features.push('Seleção de Opções');
    if (document.querySelector('.chart, canvas')) features.push('Gráficos e Visualizações');
    if (document.querySelector('table')) features.push('Tabelas de Dados');
    if (document.querySelector('.card, .document-card')) features.push('Cards Informativos');
    
    return features;
  }

  private extractForms(): FormInfo[] {
    const forms: FormInfo[] = [];
    const formElements = document.querySelectorAll('form');
    
    formElements.forEach((form, index) => {
      const inputs = form.querySelectorAll('input, textarea, select');
      const fields = Array.from(inputs).map(input => 
        input.getAttribute('placeholder') || 
        input.getAttribute('name') || 
        input.getAttribute('id') || 
        `Campo ${inputs.length}`
      );
      
      forms.push({
        id: form.id || `form-${index}`,
        type: this.inferFormType(form),
        fields,
        purpose: this.inferFormPurpose(form)
      });
    });
    
    return forms;
  }

  private extractCTAs(): CTAInfo[] {
    const ctas: CTAInfo[] = [];
    const buttons = document.querySelectorAll('button, a[class*="button"], [role="button"]');
    
    buttons.forEach(button => {
      const text = button.textContent?.trim();
      if (text && text.length > 0 && text.length < 50) {
        ctas.push({
          text,
          type: button.tagName.toLowerCase() === 'a' ? 'link' : 'button',
          purpose: this.inferCTAPurpose(text)
        });
      }
    });
    
    return ctas.slice(0, 10); // Limit to most relevant CTAs
  }

  private getContentHash(): string {
    const content = document.body.textContent || '';
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  }

  private inferFormType(form: Element): string {
    const action = form.getAttribute('action');
    const inputs = form.querySelectorAll('input');
    
    if (Array.from(inputs).some(input => input.type === 'email')) return 'login';
    if (Array.from(inputs).some(input => input.type === 'password')) return 'authentication';
    if (Array.from(inputs).some(input => input.type === 'file')) return 'upload';
    if (form.querySelector('textarea')) return 'feedback';
    
    return 'generic';
  }

  private inferFormPurpose(form: Element): string {
    const text = form.textContent?.toLowerCase() || '';
    
    if (text.includes('login') || text.includes('entrar')) return 'Fazer login';
    if (text.includes('cadastro') || text.includes('registro')) return 'Criar conta';
    if (text.includes('upload') || text.includes('enviar arquivo')) return 'Enviar arquivo';
    if (text.includes('busca') || text.includes('pesquisar')) return 'Buscar informações';
    if (text.includes('contato') || text.includes('mensagem')) return 'Entrar em contato';
    
    return 'Preencher formulário';
  }

  private inferCTAPurpose(text: string): string {
    const lower = text.toLowerCase();
    
    if (lower.includes('salvar') || lower.includes('save')) return 'Salvar dados';
    if (lower.includes('criar') || lower.includes('novo')) return 'Criar novo item';
    if (lower.includes('editar') || lower.includes('edit')) return 'Editar item';
    if (lower.includes('excluir') || lower.includes('delete')) return 'Excluir item';
    if (lower.includes('download') || lower.includes('baixar')) return 'Baixar arquivo';
    if (lower.includes('upload') || lower.includes('enviar')) return 'Enviar arquivo';
    if (lower.includes('visualizar') || lower.includes('ver')) return 'Visualizar conteúdo';
    
    return 'Executar ação';
  }

  private cleanCache(): void {
    const now = Date.now();
    Array.from(this.cache.entries()).forEach(([key, { timestamp }]) => {
      if (now - timestamp > this.cacheTimeout) {
        this.cache.delete(key);
      }
    });
  }
}

export const contextExtractionService = new ContextExtractionService();